import { useState, useMemo } from 'react';
import { useSubscriptions, useUpdateSubscription } from '../../hooks/useSubscriptions';
import { Select, useToast } from '@/components/ui';
import { SubscriptionRow } from '../../components/subscriptions/SubscriptionRow';
import type { EventSubscription } from '../../types';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All Scopes' },
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
];

export default function SubscriptionList() {
  const { data, isLoading } = useSubscriptions();
  const { success, error: showError } = useToast();
  const updateSubscription = useUpdateSubscription();

  const [statusFilter, setStatusFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');

  const subscriptions = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (scopeFilter !== 'all' && s.scope !== scopeFilter) return false;
      return true;
    });
  }, [data, statusFilter, scopeFilter]);

  const handleToggle = async (sub: EventSubscription) => {
    const newStatus = sub.status === 'active' ? 'paused' : 'active';
    try {
      await updateSubscription.mutateAsync({
        subscriptionId: sub.subscriptionId,
        status: newStatus,
      });
      success(`Subscription ${newStatus === 'paused' ? 'paused' : 'resumed'}`);
    } catch {
      showError('Failed to update subscription');
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <div className="w-48">
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e: { target: { value: string } }) =>
              setStatusFilter(e.target.value)
            }
          />
        </div>
        <div className="w-48">
          <Select
            options={SCOPE_OPTIONS}
            value={scopeFilter}
            onChange={(e: { target: { value: string } }) =>
              setScopeFilter(e.target.value)
            }
          />
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {statusFilter !== 'all' || scopeFilter !== 'all'
            ? 'No subscriptions match these filters.'
            : 'No event subscriptions found.'}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
          {subscriptions.map((sub) => (
            <SubscriptionRow
              key={sub.subscriptionId}
              subscription={sub}
              onToggle={handleToggle}
              isPending={updateSubscription.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
